/**
 * StoreFilters Component
 *
 * Filter bar for the stores tab on the search page.
 * Opens a modal/drawer with store type facet.
 * Uses client-side filtering - no re-fetching required.
 */

'use client';

import { Button } from '@/components/ui/Button';
import type { StoreHit } from '@/lib/algolia/stores';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import styles from './StoreFilters.module.scss';

export type StoreType = 'ALL' | 'BOUTIQUE' | 'FLAG_SHIP_BOUTIQUE' | 'OFFICIAL_RETAILER';

export interface StoreFiltersProps {
  /** All available stores (unfiltered) */
  stores: StoreHit[];
  /** Currently selected store type filter */
  selectedType: StoreType;
  /** Callback when filter changes */
  onTypeChange: (type: StoreType) => void;
  /** Total results count (after filtering) */
  totalResults: number;
  /** Additional class name */
  className?: string;
}

/**
 * Funnel icon for filter button
 */
function FunnelIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/**
 * Get count of stores by type
 */
function getCountByType(stores: StoreHit[], type: StoreType): number {
  if (type === 'ALL') return stores.length;
  return stores.filter((s) => s.type === type).length;
}

export function StoreFilters({
  stores,
  selectedType,
  onTypeChange,
  totalResults,
  className,
}: StoreFiltersProps) {
  const t = useTranslations('search.stores');
  const tPlp = useTranslations('plp');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const types: Array<{ value: StoreType; labelKey: string }> = [
    { value: 'ALL', labelKey: 'allTypes' },
    { value: 'FLAG_SHIP_BOUTIQUE', labelKey: 'flagship' },
    { value: 'BOUTIQUE', labelKey: 'boutique' },
    { value: 'OFFICIAL_RETAILER', labelKey: 'retailer' },
  ];

  const activeFilterCount = selectedType !== 'ALL' ? 1 : 0;

  // Get the label for the selected type
  const selectedLabel = types.find((t) => t.value === selectedType)?.labelKey || 'allTypes';

  const handleTypeSelect = (type: StoreType) => {
    onTypeChange(type);
  };

  const handleClear = () => {
    onTypeChange('ALL');
  };

  return (
    <>
      <div className={`${styles.wrapper} ${className || ''}`} data-testid="store-filters">
        <div className={styles.container}>
          {/* Filters button */}
          <button
            type="button"
            className={styles.filtersButton}
            onClick={() => setIsModalOpen(true)}
            aria-label={tPlp('openFilters')}
          >
            <span className={styles.filtersButtonText}>{tPlp('filters')}</span>
            {activeFilterCount > 0 && (
              <span className={styles.filterCount}>{activeFilterCount}</span>
            )}
            <FunnelIcon className={styles.filterIcon} />
          </button>

          {/* Active filter chip (if any) */}
          {selectedType !== 'ALL' && (
            <div className={styles.activeFilters}>
              <button
                type="button"
                onClick={handleClear}
                className={styles.chip}
                aria-label={`${tPlp('facets.remove')} ${t('storeType')}: ${t(selectedLabel)}`}
              >
                <span className={styles.chipValue}>{t(selectedLabel)}</span>
                <svg
                  className={styles.removeIcon}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}

          {/* Results count */}
          <div className={styles.resultsCount}>
            <span>{t('resultsCount', { count: totalResults })}</span>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={tPlp('filters')}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{tPlp('filters')}</h2>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setIsModalOpen(false)}
                aria-label={tPlp('facets.close')}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className={styles.modalContent}>
              {/* Store Type Facet */}
              <div className={styles.facetSection}>
                <h3 className={styles.facetTitle}>{t('storeType')}</h3>
                <div className={styles.facetOptions}>
                  {types.map(({ value, labelKey }) => {
                    const count = getCountByType(stores, value);
                    const isSelected = selectedType === value;

                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleTypeSelect(value)}
                        className={`${styles.facetOption} ${isSelected ? styles.selected : ''}`}
                        aria-pressed={isSelected}
                      >
                        <span className={styles.checkbox}>
                          {isSelected && (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                        <span className={styles.optionLabel}>{t(labelKey)}</span>
                        <span className={styles.optionCount}>({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <Button
                variant="primary"
                fullWidth
                className={styles.applyButton}
                onClick={() => setIsModalOpen(false)}
              >
                {tPlp('facets.apply')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

StoreFilters.displayName = 'StoreFilters';
